import InfiniteScrollTable from '@app/components/InfiniteScrollTable'
import SearchBar from '@app/components/SearchBar'
import { IDevelopmentClient } from '@app/models/DevelopmentClient'
import { bindFunctions } from '@app/utils/class'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import * as qs from 'query-string'
import * as React from 'react'
import DocumentTitle from 'react-document-title'
import { Link, RouteComponentProps } from 'react-router-dom'
import { Index, IndexRange, InfiniteLoader } from 'react-virtualized'
import { Button } from 'reactstrap'
import { actionCreators, IClientsListState } from '../modules/clientsList'
import './ClientListPage.scss'

type ComponentProps = IClientsListState &
  typeof actionCreators &
  RouteComponentProps<{}>

export default class ClientsListPage extends React.PureComponent<
  ComponentProps
> {
  private tableRef: React.RefObject<InfiniteScrollTable>
  private loader: InfiniteLoader | null
  private reloadTimeoutId?: number

  constructor(props: ComponentProps) {
    super(props)

    bindFunctions(this)

    this.tableRef = React.createRef<InfiniteScrollTable>()
  }

  public componentDidMount() {
    if (this.props.location.search) {
      const query = qs.parse(this.props.location.search)
      if (query.filter) {
        this._onChangeFilter(query.filter)
      }
    }

    this._loadItems(0, 50, false)
  }

  public componentDidUpdate(prevProps: ComponentProps) {
    if (this.props.location.search !== prevProps.location.search) {
      const query = qs.parse(this.props.location.search)
      if (query.filter) {
        this._onChangeFilter(query.filter)
      }
    }
  }

  public forceReloadData() {
    if (this.reloadTimeoutId) {
      clearTimeout(this.reloadTimeoutId)
    }

    this.reloadTimeoutId = window.setTimeout(() => {
      if (this.loader) {
        this.loader.resetLoadMoreRowsCache(true)
      }
      this._loadItems(0, 50, false).then(() => {
        // load more items
      })
    }, 250)
  }

  public render() {
    return (
      <DocumentTitle title='Development Clients'>
        <div className='container d-flex flex-column h-100 pt-3'>
          <div>
            <div className='d-flex flex-row justify-content-between'>
              <h3>Development Clients</h3>
              <Link to='/development/client/add' className='align-self-center'>
                <Button size='sm' color='primary'>
                  <FontAwesomeIcon icon={['fal', 'plus']} fixedWidth />
                  <span>Add Client</span>
                </Button>
              </Link>
            </div>
            <SearchBar
              onChange={this._onChangeFilter}
              placeholder='Looking for a client?'
              size='sm'
              value={this.props.filterText}
            />
          </div>
          <div className='mt-4'>
            <div className='row'>
              {this.props.items.map((a: any, i: any) => {
                const client = a as IDevelopmentClient

                if (!client.logo || !client.logo.id) {
                  return null
                }

                return (
                  <div
                    key={client.id}
                    className='col development-client-card'
                    style={{ flexGrow: 0, cursor: 'pointer' }}
                    onClick={() => this._onRowClicked(a, i)}
                  >
                    <div className='inner-container'>
                      {client.logo.availableSizes ? (
                        <img
                          className='logo-image'
                          src={`${client.logo.baseStorageUrl}/${
                            client.logo.availableSizes.h500w500
                          }`}
                          width={300}
                        />
                      ) : (
                        <img
                          src={`${client.logo.baseStorageUrl}/${
                            client.logo.id
                          }${client.logo.extension}`}
                          width={300}
                        />
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      </DocumentTitle>
    )
  }

  private _isRowLoaded({ index }: Index): boolean {
    if (this.props.items.size <= index) {
      return false
    }
    return !!this._getItem(index)
  }

  private _getItem(index: number): any {
    return this.props.items.get(index)
  }

  private _loadItems(
    startIndex: number,
    stopIndex: number,
    append: boolean,
  ): Promise<void> {
    return new Promise((resolve) => {
      this.props.loadClients(startIndex, stopIndex, append, resolve)
    })
  }

  private _loadMoreRows({ startIndex, stopIndex }: IndexRange): Promise<void> {
    if (this.props.isLoading) {
      return Promise.resolve()
    }

    return this._loadItems(startIndex, stopIndex, true)
  }

  private _noRowsRenderer() {
    return <div>No clients</div>
  }

  private _onRowClicked(item: IDevelopmentClient, index: number) {
    this.props.history.push(`/development/clients/${item.slug}/sites`)
  }

  private _onChangeFilter(newFilter: string) {
    this.props.setFilterText(newFilter)
    this._loadItems(0, 50, false)
  }

  private _reloadData() {
    if (this.tableRef.current) {
      this.tableRef.current.forceReloadData()
    }
  }
}
